import { ActionTree, Commit, Dispatch } from "vuex";
import axios from "axios";

import { store, State } from "@/store";

import {
  AccordianState,
  ColorPickrState,
  UserArtworkFetch,
  SettingsState,
  LabelSizes,
  LabelShapes,
  ClientDimensions,
  UrlProperties,
  LabelSVG,
} from "@/types";

import { MutationTypes } from "@/store/mutations";

import { getEditorUrlParam } from "@/composables/useParseURL";

export enum ActionTypes {
  SET_MODAL = "SET_MODAL",
  FETCH_SHOW_ONBOARDING = "FETCH_SHOW_ONBOARDING",
  SET_SHOW_ONBOARDING = "SET_SHOW_ONBOARDING",

  FETCH_USER_ARTWORK = "FETCH_USER_ARTWORK",
  SET_CUSTOM_LABEL = "SET_CUSTOM_LABEL",
  FETCH_EDITOR_PARAMS = "FETCH_EDITOR_PARAMS",
  FETCH_TEMPLATE_AND_LABEL = "FETCH_TEMPLATE_AND_LABEL",
  FETCH_CUSTOM_LABEL_SIZES = "FETCH_CUSTOM_LABEL_SIZES",
  FETCH_LABEL_SIZES = "FETCH_LABEL_SIZES",
  FETCH_LABEL_SHAPES = "FETCH_LABEL_SHAPES",
  FETCH_MATERIALS = "FETCH_MATERIALS",

  CHANGE_LABEL_SIZE = "CHANGE_LABEL_SIZE",
  CHANGE_CUSTOM_LABEL_SIZE = "CHANGE_CUSTOM_LABEL_SIZE",
  CHANGE_LABEL_SHAPE = "CHANGE_LABEL_SHAPE",
  CHANGE_LABEL_MATERIAL = "CHANGE_LABEL_MATERIAL",

  SET_MOUNTING_LABEL = "SET_MOUNTING_LABEL",
  SET_CLIENT_DIMENSIONS = "SET_CLIENT_DIMENSIONS",
  SET_LABEL_BG_COLOR = "SET_LABEL_BG_COLOR",
  SET_USER_ARTWORK_FETCH = "SET_USER_ARTWORK_FETCH",
  SET_ACCORDIAN_STATE = "SET_ACCORDIAN_STATE",
  SET_COLOR_PICKR_STATE = "SET_COLOR_PICKR_STATE",
  SET_CANVAS_ORIENTATION = "SET_CANVAS_ORIENTATION",
  SET_SETTINGS_STATE = "SET_SETTINGS_STATE",
  SET_CANVAS_ACTIVE_OBJECT = "SET_CANVAS_ACTIVE_OBJECT",
  SET_LABEL_SVG = "SET_LABEL_SVG",
}
//prettier-ignore
export interface Actions {
  [ActionTypes.SET_MODAL]({ commit }: { commit: Commit }, payload: string): void;
  [ActionTypes.FETCH_SHOW_ONBOARDING]({ commit }: { commit: Commit }): Promise<void>;
  [ActionTypes.SET_SHOW_ONBOARDING]({ commit }: { commit: Commit }, payload: boolean ): void;

	[ActionTypes.FETCH_USER_ARTWORK](
		{ commit, dispatch }: { commit: Commit; dispatch: Dispatch },
		{ pathName, urlProperties, hash }: { pathName: string; urlProperties: UrlProperties; hash: string; }
	): Promise<void>;
	
	[ActionTypes.SET_CUSTOM_LABEL](
		{ commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { label, labelType }: { label: any; labelType: string }
	): void;

	[ActionTypes.FETCH_EDITOR_PARAMS](
		{ commit, dispatch }: { commit: Commit; dispatch: Dispatch },
		{ pathName, urlProperties }: { pathName: string; urlProperties: UrlProperties }
	): Promise<void>;

	[ActionTypes.FETCH_TEMPLATE_AND_LABEL](
		{ commit, dispatch }: { commit: Commit; dispatch: Dispatch; }, 
    { urlProperties, templateId }: { templateId: string; urlProperties: UrlProperties }
	): Promise<void>;

	[ActionTypes.FETCH_CUSTOM_LABEL_SIZES](
		{ commit }: { commit: Commit },
        { labelType, shapeId, setDefault, }: { labelType: string; shapeId: number; setDefault?: boolean }
	): Promise<void>;

	[ActionTypes.FETCH_LABEL_SIZES](
		{ commit }: { commit: Commit },
		{ labelType, shapeId, setDefault, }: { labelType: string; shapeId: number; setDefault?: boolean }
	): Promise<void>;

	[ActionTypes.FETCH_LABEL_SHAPES](
		{ commit }: { commit: Commit },
		{ labelType }: { labelType: string }
	): Promise<void>;

    [ActionTypes.FETCH_MATERIALS](
		{ commit }: { commit: Commit },
		{ materialParams }: { materialParams: string }
	): Promise<void>;

	[ActionTypes.CHANGE_LABEL_SIZE](
		{ commit }: { commit: Commit; },
		selectedLabel: LabelSizes
	): Promise<void>;

	[ActionTypes.CHANGE_CUSTOM_LABEL_SIZE](
		{ commit }: { commit: Commit },
		{ dimension, value }: { dimension: string; value: number }
	): Promise<void>;

	[ActionTypes.CHANGE_LABEL_SHAPE](
		{ dispatch }: { dispatch: Dispatch },
		selectedLabel: LabelShapes
	): Promise<void>;

  [ActionTypes.CHANGE_LABEL_MATERIAL]({ commit }: { commit: Commit }, selectedMaterial: any ): void;

  [ActionTypes.SET_MOUNTING_LABEL]({ commit }: { commit: Commit }, payload: boolean): void;
  
  [ActionTypes.SET_CLIENT_DIMENSIONS]({ commit }: { commit: Commit }, payload: ClientDimensions): void;

	[ActionTypes.SET_LABEL_BG_COLOR]({ commit }: { commit: Commit }, payload: string): void;

	[ActionTypes.SET_USER_ARTWORK_FETCH](
		{ commit }: { commit: Commit },
		payload: UserArtworkFetch
	): void;

	[ActionTypes.SET_ACCORDIAN_STATE](
		{ commit }: { commit: Commit },
		payload: AccordianState
	): void;

	[ActionTypes.SET_COLOR_PICKR_STATE](
		{ commit }: { commit: Commit },
		payload: ColorPickrState
	): void;

	[ActionTypes.SET_SETTINGS_STATE]({ commit }: { commit: Commit }, payload: string | SettingsState): void;
	[ActionTypes.SET_CANVAS_ORIENTATION]({ commit }: { commit: Commit }, payload: string): void;
	[ActionTypes.SET_CANVAS_ACTIVE_OBJECT]({ commit }: { commit: Commit }, payload: fabric.Object): void;
	[ActionTypes.SET_LABEL_SVG](
		{ commit }: { commit: Commit },
		payload: LabelSVG
	): void;
}

export const actions: ActionTree<State, State> & Actions = {
  [ActionTypes.SET_MODAL]({ commit }: { commit: Commit }, payload: string): void {
    commit(MutationTypes.SET_MODAL, payload);
  },
  async [ActionTypes.FETCH_SHOW_ONBOARDING]({ commit }: { commit: Commit }): Promise<void> {
    try {
      const res = await axios.get(
        `${process.env.VUE_APP_API_URL}/editor/customers/ajax.php?mode=show_onboarding`
      );

      const show = res.data.show;

      if (!show) {
        commit(MutationTypes.SET_SHOW_ONBOARDING, false);
        return;
      }

      if (document.cookie.split(";").some((item) => item.includes("OBC="))) {
        commit(MutationTypes.SET_SHOW_ONBOARDING, false);
        return;
      }

      commit(MutationTypes.SET_SHOW_ONBOARDING, show);
    } catch (e) {
      console.log(e);
      commit(MutationTypes.SET_SHOW_ONBOARDING, false);
    }
  },
  [ActionTypes.SET_SHOW_ONBOARDING]({ commit }: { commit: Commit }, payload: boolean): void {
    commit(MutationTypes.SET_SHOW_ONBOARDING, payload);
  },
  // prettier-ignore
  async [ActionTypes.FETCH_USER_ARTWORK](
		{ commit, dispatch }: { commit: Commit; dispatch: Dispatch },
		{ pathName, urlProperties, hash, }: { pathName: string; urlProperties: UrlProperties; hash: string }
	): Promise<void> {
		commit(MutationTypes.START_FETCHING_USER_ARTWORK);

		try {
			const res = await axios.get(
				`${process.env.VUE_APP_API_URL}/editor/artworks/ajax.php?action=get_artwork&hash=${hash}&path=${pathName}&type=${urlProperties.labelType}`
			);

			switch (urlProperties.labelType) {
				case "flyer": {
					if (res.data.label) {
						if (urlProperties.corners === "rounded") {
							res.data.label.corners = "Rounded";
						}
					}

					commit(MutationTypes.FETCH_USER_ARTWORK_SUCCESS, {
						urlProperties,
						data: res.data,
					});
					break;
				}
				case "magnet": {
					if (urlProperties.isCustom) {
						const params = res.data.params;

						const label = {
							name: `${params.width} x ${params.height}`,
							title: `${params.width} x ${params.height}`,
							corners: urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded" ? "Rounded" : "None",
							corner_radius: urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded" ? ".125" : "0",
							shape_id: Number(params.shape_id),
							id: 0,
							width: Number(params.width),
							height: Number(params.height),
						};

						dispatch(ActionTypes.SET_CUSTOM_LABEL, { label, labelType: urlProperties.labelType });

						commit(MutationTypes.FETCH_USER_ARTWORK_SUCCESS, {
							urlProperties,
							data: res.data,
						});
					} else {
						if (res.data.label) {
							if (urlProperties.corners === "rounded") {
								res.data.label.corners = "Rounded";
							}
						}

						commit(MutationTypes.FETCH_USER_ARTWORK_SUCCESS, {
							urlProperties,
							data: res.data,
						});
					}
					break;
				}
				case "printed":
				case "rollprinted": {
					if (urlProperties.isCustom) {
						const params = res.data.params;

						const label = {
							name: `${params.width} x ${params.height}`,
							title: `${params.width} x ${params.height}`,
							corners: urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded" ? "Rounded" : "None",
							corner_radius: urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded" ? ".125" : "0",
							shape_id: Number(params.shape_id),
							id: 0,
							width: Number(params.width),
							height: Number(params.height),
						};

						dispatch(ActionTypes.SET_CUSTOM_LABEL, { label, labelType: urlProperties.labelType });
						dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: urlProperties.labelType });

						commit(MutationTypes.FETCH_USER_ARTWORK_SUCCESS, {
							urlProperties,
							data: res.data,
						});
					} else {
						dispatch(ActionTypes.FETCH_LABEL_SIZES, {
							labelType: urlProperties.labelType,
							shapeId: Number(res.data.params.shape_id),
						});
						dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: urlProperties.labelType });

						if (res.data.label) {
							if (urlProperties.corners === "rounded") {
								res.data.label.corners = "Rounded";
							}
						}

						commit(MutationTypes.FETCH_USER_ARTWORK_SUCCESS, {
							urlProperties,
							data: res.data,
						});
					}

          const materialParams = getMaterialParams(res.data.params, urlProperties);
					dispatch(ActionTypes.FETCH_MATERIALS, { materialParams });
					break;
				}
			}
		} catch (e) {
			console.log(e);
			commit(MutationTypes.FETCH_USER_ARTWORK_FAILURE, e);
		}
	},
  [ActionTypes.SET_CUSTOM_LABEL](
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    { label, labelType }: { label: any; labelType: string }
  ): void {
    switch (labelType) {
      case "magnet": {
        commit(MutationTypes.SET_CUSTOM_LABEL, { labelType: "magnet", label });
        break;
      }
      case "printed": {
        dispatch(ActionTypes.FETCH_CUSTOM_LABEL_SIZES, {
          labelType: "custom-printed",
          shapeId: label.shape_id,
        });
        commit(MutationTypes.SET_CUSTOM_LABEL, { labelType: "custom-printed", label });
        break;
      }
      case "rollprinted": {
        dispatch(ActionTypes.FETCH_CUSTOM_LABEL_SIZES, {
          labelType: "custom-rollprinted",
          shapeId: label.shape_id,
        });
        commit(MutationTypes.SET_CUSTOM_LABEL, { labelType: "custom-rollprinted", label });
        break;
      }
    }
  },
  async [ActionTypes.FETCH_EDITOR_PARAMS](
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    { pathName, urlProperties }: { pathName: string; urlProperties: UrlProperties }
  ): Promise<void> {
    commit(MutationTypes.START_FETCHING_EDITOR_PARAMS);

    try {
      const res = await axios.get(
        `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=extractEditorVueParams&path=${pathName}&type=${urlProperties.labelType}`
      );

      switch (urlProperties.labelType) {
        case "blank": {
          dispatch(ActionTypes.FETCH_LABEL_SIZES, {
            labelType: urlProperties.labelType,
            shapeId: Number(res.data.params.shape_id),
          });

          dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: urlProperties.labelType });

          if (res.data.label) {
            if (urlProperties.corners === "rounded") {
              res.data.label.corners = "Rounded";
            }

            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: res.data.label,
            });
          } else {
            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: undefined,
            });
          }

          const materialParams = getMaterialParams(res.data.params, urlProperties);
          dispatch(ActionTypes.FETCH_MATERIALS, { materialParams });
          break;
        }
        case "flyer": {
          if (res.data.label) {
            if (urlProperties.corners === "rounded") {
              res.data.label.corners = "Rounded";
            }

            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: res.data.label,
            });
          } else {
            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: undefined,
            });
          }
          break;
        }
        case "magnet": {
          if (urlProperties.isCustom) {
            const params = res.data.params;

            const label = {
              name: `${params.width} x ${params.height}`,
              title: `${params.width} x ${params.height}`,
              corners:
                urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded"
                  ? "Rounded"
                  : "None",
              corner_radius:
                urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded"
                  ? ".125"
                  : "0",
              shape_id: Number(params.shape_id),
              id: 0,
              width: Number(params.width),
              height: Number(params.height),
            };

            dispatch(ActionTypes.SET_CUSTOM_LABEL, { label, labelType: urlProperties.labelType });

            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: undefined,
            });
          } else {
            if (res.data.label) {
              if (urlProperties.corners === "rounded") {
                res.data.label.corners = "Rounded";
              }

              commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
                labelType: urlProperties.labelType,
                params: res.data.params,
                label: res.data.label,
              });
            } else {
              commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
                labelType: urlProperties.labelType,
                params: res.data.params,
                label: undefined,
              });
            }
          }

          const materialParams = getMaterialParams(res.data.params, urlProperties);
          dispatch(ActionTypes.FETCH_MATERIALS, { materialParams });
          break;
        }
        case "printed":
        case "rollprinted": {
          if (urlProperties.isCustom) {
            const params = res.data.params;

            const label = {
              name: `${params.width} x ${params.height}`,
              title: `${params.width} x ${params.height}`,
              corners:
                urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded"
                  ? "Rounded"
                  : "None",
              corner_radius:
                urlProperties.corners.length && urlProperties.corners?.toLowerCase() === "rounded"
                  ? ".125"
                  : "0",
              shape_id: Number(params.shape_id),
              id: 0,
              width: Number(params.width),
              height: Number(params.height),
            };

            dispatch(ActionTypes.SET_CUSTOM_LABEL, { label, labelType: urlProperties.labelType });
            dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: urlProperties.labelType });

            commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
              labelType: urlProperties.labelType,
              params: res.data.params,
              label: undefined,
            });
          } else {
            dispatch(ActionTypes.FETCH_LABEL_SIZES, {
              labelType: urlProperties.labelType,
              shapeId: Number(res.data.params.shape_id),
            });
            dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: urlProperties.labelType });

            if (res.data.label) {
              if (urlProperties.corners === "rounded") {
                res.data.label.corners = "Rounded";
              }

              commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
                labelType: urlProperties.labelType,
                params: res.data.params,
                label: res.data.label,
              });
            } else {
              commit(MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS, {
                labelType: urlProperties.labelType,
                params: res.data.params,
                label: undefined,
              });
            }
          }

          const materialParams = getMaterialParams(res.data.params, urlProperties);
          dispatch(ActionTypes.FETCH_MATERIALS, { materialParams });
          break;
        }
      }
    } catch (e) {
      console.log(e);
      commit(MutationTypes.FETCH_EDITOR_PARAMS_FAILURE, e);
    }
  },
  async [ActionTypes.FETCH_TEMPLATE_AND_LABEL](
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    { urlProperties, templateId }: { templateId: string; urlProperties: UrlProperties }
  ): Promise<void> {
    commit(MutationTypes.START_TEMPLATE_AND_LABEL);

    try {
      const res = await axios.get(
        `${process.env.VUE_APP_API_URL}/editor/templates/ajax.php?action=getTemplateAndParams&template_id=${templateId}`
      );

      const label = res.data.label;
      const template = res.data.template;
      const templateJson = res.data.template_json;
      const templateParams = res.data.template_params;

      if (templateParams && templateParams.product_type !== "flyer") {
        dispatch(ActionTypes.FETCH_LABEL_SIZES, {
          labelType: templateParams.product_type,
          shapeId: Number(templateParams.shape_id),
        });
        dispatch(ActionTypes.FETCH_LABEL_SHAPES, { labelType: templateParams.product_type });

        const materialParams = getMaterialParams(templateParams, urlProperties);

        dispatch(ActionTypes.FETCH_MATERIALS, { materialParams });
      }

      commit(MutationTypes.TEMPLATE_AND_LABEL_SUCCESS, {
        label,
        template,
        templateJson,
        templateParams,
      });
    } catch (e) {
      console.log(e);
      commit(MutationTypes.TEMPLATE_AND_LABEL_FAILURE, e);
    }
  },
  async [ActionTypes.FETCH_LABEL_SIZES](
    { commit }: { commit: Commit },
    { labelType, shapeId, setDefault }: { labelType: string; shapeId: number; setDefault?: boolean }
  ): Promise<void> {
    commit(MutationTypes.START_FETCHING_SIZES);
    try {
      switch (labelType) {
        case "blank":
        case "printed": {
          const res = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchLabels&sefu=printed/sheet-labels&shape_id=${shapeId}&size_option=standard`
          );

          const data = Object.values<any>(res.data.labels);

          if (setDefault) {
            commit(MutationTypes.FETCH_SIZES_SUCCESS, { data, shapeId, setDefault });
          } else {
            commit(MutationTypes.FETCH_SIZES_SUCCESS, { data });
          }

          break;
        }
        case "rollprinted": {
          const res = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchLabels&sefu=printed/roll-labels&shape_id=${shapeId}&size_option=standard`
          );

          const data = Object.values<any>(res.data.labels);

          if (setDefault) {
            commit(MutationTypes.FETCH_SIZES_SUCCESS, { data, shapeId, setDefault });
          } else {
            commit(MutationTypes.FETCH_SIZES_SUCCESS, { data });
          }
          break;
        }
      }
    } catch (e) {
      console.log(e);
      commit(MutationTypes.FETCH_SIZES_FAILURE, e);
    }
  },
  async [ActionTypes.FETCH_CUSTOM_LABEL_SIZES](
    { commit }: { commit: Commit },
    { labelType, shapeId, setDefault }: { labelType: string; shapeId: number; setDefault?: boolean }
  ): Promise<void> {
    commit(MutationTypes.START_FETCHING_CUSTOM_SIZES);

    try {
      switch (labelType) {
        case "custom-printed": {
          const res = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchLabels&sefu=printed/sheet-labels&shape_id=${shapeId}&size_option=custom`
          );

          const data = Object.values<any>(res.data.labels);

          commit(MutationTypes.FETCH_CUSTOM_SIZES_SUCCESS, { data, shapeId, setDefault });
          break;
        }
        case "custom-rollprinted": {
          const res = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchLabels&sefu=printed/roll-labels&shape_id=${shapeId}&size_option=custom`
          );

          const data = Object.values<any>(res.data.labels);

          commit(MutationTypes.FETCH_CUSTOM_SIZES_SUCCESS, { data, shapeId, setDefault });
          break;
        }
      }
    } catch (e) {
      console.log(e);
      commit(MutationTypes.FETCH_CUSTOM_SIZES_FAILURE, e);
    }
  },
  async [ActionTypes.FETCH_LABEL_SHAPES]({ commit }, { labelType }): Promise<void> {
    commit(MutationTypes.START_FETCHING_SHAPES);

    try {
      const res = await axios.get(
        `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchShapesEditorVue&sefu=${labelType}`
      );

      const data = Object.values(res.data.shapes);

      commit(MutationTypes.FETCH_SHAPES_SUCCESS, data);
    } catch (e) {
      console.log(e);
      commit(MutationTypes.FETCH_SHAPES_FAILURE, e);
    }
  },
  async [ActionTypes.FETCH_MATERIALS](
    { commit }: { commit: Commit },
    { materialParams }: { materialParams: string }
  ): Promise<void> {
    commit(MutationTypes.START_FETCHING_MATERIALS);

    if (materialParams.includes("rollprinted")) {
      materialParams = "printed/roll-labels";
    }

    try {
      const res = await axios.get(
        `${process.env.VUE_APP_API_URL}/editor/products/ajax.php?action=fetchMaterials&sefu=${materialParams}`
      );

      const data = Object.values(res.data.materials);

      const materialId = getEditorUrlParam("material_id");

      commit(MutationTypes.FETCH_MATERIALS_SUCCESS, { data, materialId });
    } catch (e) {
      console.log(e);
      commit(MutationTypes.FETCH_MATERIALS_FAILURE, e);
    }
  },

  async [ActionTypes.CHANGE_LABEL_SIZE](
    { commit }: { commit: Commit },
    selectedLabel: LabelSizes
  ): Promise<void> {
    const foundLabelSize: LabelSizes = store.getters.labelSizes.find((label: LabelSizes):
      | LabelSizes
      | undefined => {
      return label.name === selectedLabel.name ? label : undefined;
    });

    if (foundLabelSize) {
      return new Promise<void>((resolve) => {
        commit(MutationTypes.CHANGE_LABEL_SIZE, foundLabelSize);

        resolve();
      });
    } else {
      return new Promise<void>((_, reject) => {
        const error = new Error("Could not find label size");
        reject(error);
      });
    }
  },
  async [ActionTypes.CHANGE_CUSTOM_LABEL_SIZE](
    { commit }: { commit: Commit },
    { dimension, value }: { dimension: string; value: number }
  ): Promise<void> {
    const currentLabel = store.getters.currentLabel;

    if (dimension === "width") {
      const label = {
        name: `${value} x ${currentLabel.height}`,
        title: `${value} x ${currentLabel.height}`,
        corners: currentLabel.corners,
        corner_radius: currentLabel.corner_radius,
        shape_id: currentLabel.shape_id,
        label_id: currentLabel.label_id,
        width: value,
        height: currentLabel.height,
      };

      return new Promise<void>((resolve) => {
        commit(MutationTypes.CHANGE_CUSTOM_LABEL_SIZE, label);

        resolve();
      });
    } else {
      const label = {
        name: `${currentLabel.width} x ${value}`,
        title: `${currentLabel.width} x ${value}`,
        corners: currentLabel.corners,
        corner_radius: currentLabel.corner_radius,
        shape_id: currentLabel.shape_id,
        label_id: currentLabel.label_id,
        width: currentLabel.width,
        height: value,
      };

      return new Promise<void>((resolve) => {
        commit(MutationTypes.CHANGE_CUSTOM_LABEL_SIZE, label);

        resolve();
      });
    }
  },
  async [ActionTypes.CHANGE_LABEL_SHAPE](
    { dispatch }: { dispatch: Dispatch },
    selectedShape: LabelShapes
  ): Promise<void> {
    const currentLabelType = store.getters.currentLabelType;
    const foundLabelShape = store.getters.labelShapes.find((labelShape: LabelShapes):
      | LabelShapes
      | undefined => {
      return labelShape.name === selectedShape.name ? labelShape : undefined;
    });

    if (foundLabelShape) {
      switch (currentLabelType) {
        case "printed":
        case "rollprinted": {
          dispatch(ActionTypes.FETCH_LABEL_SIZES, {
            labelType: currentLabelType,
            shapeId: foundLabelShape.id,
            setDefault: true,
          });
          break;
        }
        case "custom-printed":
        case "custom-rollprinted": {
          dispatch(ActionTypes.FETCH_CUSTOM_LABEL_SIZES, {
            labelType: currentLabelType,
            shapeId: foundLabelShape.id,
            setDefault: true,
          });
          break;
        }
      }

      return new Promise<void>((resolve) => {
        resolve();
      });
    } else {
      return new Promise<void>((_, reject) => {
        const error = new Error("Could not change label shape");
        reject(error);
      });
    }
  },

  [ActionTypes.CHANGE_LABEL_MATERIAL]({ commit }: { commit: Commit }, selectedMaterial: any): void {
    commit(MutationTypes.CHANGE_LABEL_MATERIAL, selectedMaterial);
  },
  [ActionTypes.SET_MOUNTING_LABEL]({ commit }: { commit: Commit }, payload: boolean): void {
    commit(MutationTypes.SET_MOUNTING_LABEL, payload);
  },
  [ActionTypes.SET_CLIENT_DIMENSIONS](
    { commit }: { commit: Commit },
    payload: ClientDimensions
  ): void {
    commit(MutationTypes.SET_CLIENT_DIMENSIONS, payload);
  },
  [ActionTypes.SET_LABEL_BG_COLOR]({ commit }: { commit: Commit }, payload: string): void {
    commit(MutationTypes.SET_LABEL_BG_COLOR, payload);
  },
  [ActionTypes.SET_USER_ARTWORK_FETCH](
    { commit }: { commit: Commit },
    payload: UserArtworkFetch
  ): void {
    commit(MutationTypes.SET_USER_ARTWORK_FETCH, payload);
  },
  [ActionTypes.SET_ACCORDIAN_STATE]({ commit }: { commit: Commit }, payload: AccordianState): void {
    commit(MutationTypes.SET_ACCORDIAN_STATE, payload);
  },
  [ActionTypes.SET_COLOR_PICKR_STATE](
    { commit }: { commit: Commit },
    payload: ColorPickrState
  ): void {
    commit(MutationTypes.SET_COLOR_PICKR_STATE, payload);
  },
  [ActionTypes.SET_SETTINGS_STATE](
    { commit }: { commit: Commit },
    payload: string | SettingsState
  ): void {
    commit(MutationTypes.SET_SETTINGS_STATE, payload);
  },
  [ActionTypes.SET_CANVAS_ORIENTATION]({ commit }: { commit: Commit }, payload: string): void {
    commit(MutationTypes.SET_CANVAS_ORIENTATION, payload);
  },
  [ActionTypes.SET_CANVAS_ACTIVE_OBJECT](
    { commit }: { commit: Commit },
    payload: fabric.Object
  ): void {
    commit(MutationTypes.SET_CANVAS_ACTIVE_OBJECT, payload);
  },
  [ActionTypes.SET_LABEL_SVG]({ commit }: { commit: Commit }, payload: LabelSVG): void {
    commit(MutationTypes.SET_LABEL_SVG, payload);
  },
};

const getMaterialParams = (labelParams: any, urlProperties: UrlProperties) => {
  let materialParams = "";

  materialParams += "&sefu=" + window.location.pathname.substring(1);

  if (labelParams.label_id) {
    materialParams += "&label_id=" + labelParams.label_id;
  } else {
    materialParams += "&label_id=12";
  }

  if (labelParams.product_id) {
    materialParams += "&product_id=" + labelParams.product_id;
  }

  if (urlProperties.isCustom) {
    materialParams += "&size_option=custom";
  } else {
    materialParams += "&size_option=standard";
  }

  materialParams += "&width=" + (labelParams.width ? labelParams.width : "");
  materialParams += "&height=" + (labelParams.height ? labelParams.height : "");
  materialParams += "&height=" + (labelParams.height ? labelParams.height : "");

  if (getEditorUrlParam("secProduct")) {
    materialParams += "&secProduct=" + getEditorUrlParam("secProduct");
  }

  return materialParams;
};
